import { useState, useEffect, memo } from 'react'

import { useNavigate } from 'react-router-dom'
import { langRigs } from '../assets/langRigs'
import {
  useSessionWS,
  useRestricted,
  useTrkptsWS,
  useUser,
  useLang,
  useListSessionsWS,
  useListBoards,
  useListSails,
} from '../utils/hooks'
import RigsChart from '../components/RigsChart'
import Help from '../components/Help'
import HelpNotif from '../components/HelpNotif'

import { getChartData, getSessionData } from '../assets/assets_functions'

import { Loader, LoaderWrapper } from '../components/StyledElements'
import RigsOptions from '../components/RigsOptions'
import { cloneDeep } from 'lodash'
import { useRef } from 'react'
import { kRigsUsedView } from '../assets/colors'

const Rigs = ({ data, handleUpdateStats }) => {
  const firstDiv = useRef(null)

  const navigateTo = useNavigate()

  const { user } = useUser()
  const userId = user.userId

  const { lang } = useLang()
  const choosenLang = lang === 'fr' ? langRigs.fr : langRigs.eng
  const demoUserName = process.env.REACT_APP_DEMO_USERNAME

  const { sessionWS, changeSessionWS } = useSessionWS()
  const { listSessionsWS, setListSessionsWS } = useListSessionsWS()
  const { listBoards } = useListBoards()
  const { listSails } = useListSails()
  const { trkptsWS } = useTrkptsWS()
  const [listRigs, setListRigs] = useState([])
  const [chartOptions, setChartOptions] = useState(null)
  const [rigsTimeDomain, setRigsTimeDomain] = useState(null)
  const [sliderValues, setSliderValues] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const { restricted } = useRestricted()
  const [canBeSaved, setCanBeSaved] = useState(false)

  const [showHelp, setShowHelp] = useState(false)

  useEffect(() => {
    if (!sessionWS) {
      const localUserId = localStorage.getItem('wsurf_userId')
      if (!localUserId || Number(localUserId) === -1) navigateTo('/')
      else navigateTo('/sessions/')
    }
  }, [])

  useEffect(() => {
    if (firstDiv.current !== null) {
      const topScroll = firstDiv.current.getBoundingClientRect().top
      window.scrollTo({ top: topScroll.current - 120, behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    if (!sessionWS) return
    const { timeDomain, restrictedTimeDomain } = getSessionData(trkptsWS.brut)
    setRigsTimeDomain({ brut: timeDomain, restricted: restrictedTimeDomain })
  }, [trkptsWS.brut])

  useEffect(() => {
    if (listRigs.length > 0) return
    if (!rigsTimeDomain) return
    setIsLoading(true)

    let rigsData = data.rigsData
    let tempSliderValues = []
    if (rigsData.length > 0) {
      tempSliderValues = [+rigsData[0].start]
      rigsData.forEach((rig) => {
        tempSliderValues.push(+rig.end)
      })
    } else {
      tempSliderValues.push(+rigsTimeDomain.restricted[0])
      tempSliderValues.push(+rigsTimeDomain.restricted[1])
      rigsData.push({
        _id: -1,
        boardId: -1,
        sailId: -1,
        start: rigsTimeDomain.restricted[0],
        end: rigsTimeDomain.restricted[1],
        sessionId: sessionWS._id,
        userId: userId,
      })
    }
    const { chartOptions } = getChartData(
      trkptsWS.brut,
      sessionWS.timeZone,
      restricted,
      'rigs',
      null,
    )
    setListRigs(rigsData)
    setChartOptions(chartOptions)
    setSliderValues(tempSliderValues)
    setIsLoading(false)
  }, [rigsTimeDomain])

  useEffect(() => {
    if (!listRigs) return
    const tempSaveDisabled =
      listRigs.length > 0
        ? !(
            listRigs.find((rig) => rig.boardId === -1) === undefined &&
            listRigs.find((rig) => rig.sailId === -1) === undefined
          )
        : false
    setCanBeSaved(!tempSaveDisabled)
  }, [listRigs])

  const handleSliderChange = (ms) => {
    let tempListRigs = cloneDeep(listRigs)
    for (let i = 0; i < ms.length; i++) {
      if (i === 0) {
        tempListRigs[0].start = ms[i]
      } else if (i === ms.length - 1) {
        tempListRigs[i - 1].end = ms[i]
      } else {
        tempListRigs[i - 1].end = ms[i]
        tempListRigs[i].start = ms[i]
      }
    }
    setListRigs(tempListRigs)
    setSliderValues(ms)
  }

  const handleRigsUpdate = (data) => {
    setListRigs(data)
  }

  const handleAddRig = () => {
    let tempListRigs
    let tempSliderValues
    if (listRigs.length > 0) {
      tempListRigs = cloneDeep(listRigs)
      tempSliderValues = cloneDeep(sliderValues)
      let newValueStart
      let newValueEnd
      if (
        tempListRigs[tempListRigs.length - 1].end ===
        rigsTimeDomain.restricted[1]
      ) {
        newValueStart =
          (tempListRigs[tempListRigs.length - 1].start +
            tempListRigs[tempListRigs.length - 1].end) /
          2
        newValueEnd = tempListRigs[tempListRigs.length - 1].end
      } else {
        newValueStart = tempListRigs[tempListRigs.length - 1].end
        newValueEnd = rigsTimeDomain.restricted[1]
      }

      tempListRigs.push({
        _id: -1,
        boardId: -1,
        sailId: -1,
        start: newValueStart,
        end: newValueEnd,
        sessionId: sessionWS._id,
        userId: userId,
      })
      tempListRigs[tempListRigs.length - 2].end = newValueStart
      tempSliderValues[tempSliderValues.length - 1] = newValueStart
      tempSliderValues.push(newValueEnd)
    } else {
      tempListRigs = []
      tempSliderValues = []
      tempListRigs.push({
        _id: -1,
        boardId: -1,
        sailId: -1,
        start: rigsTimeDomain.restricted[0],
        end: rigsTimeDomain.restricted[1],
        sessionId: sessionWS._id,
        userId: userId,
      })
      tempSliderValues.push(rigsTimeDomain.restricted[0])
      tempSliderValues.push(rigsTimeDomain.restricted[1])
    }

    setListRigs(tempListRigs)
    setSliderValues(tempSliderValues)
  }

  const handleDelRig = () => {
    const newValueEnd = sliderValues[sliderValues.length - 1]
    let tempListRigs = cloneDeep(listRigs)
    let tempSliderValues = cloneDeep(sliderValues)
    if (tempListRigs.length > 1) {
      tempSliderValues.splice(-1, 1)
      tempSliderValues[tempSliderValues.length - 1] = newValueEnd
      tempListRigs.splice(-1, 1)
      tempListRigs[tempListRigs.length - 1].end = newValueEnd
    } else {
      tempSliderValues = []
      tempListRigs = []
    }
    setListRigs(tempListRigs)
    setSliderValues(tempSliderValues)
  }

  const callAPICreateRig = (rig) => {
    const url = '/api/rig/createRig'
    const new_rig = {
      sessionId: rig.sessionId,
      boardId: rig.boardId,
      sailId: rig.sailId,
      start: rig.start,
      end: rig.end,
      userId: rig.userId,
    }
    return new Promise((resolve, reject) => {
      fetch(url, {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rig: new_rig }),
      })
        .then((response) => response.json())
        .then((resRig) => {
          console.log('rig créé : ' + resRig)
          resolve(resRig)
        })
        .catch((error) => {
          console.log('Error creating rig ' + rig, error)
          reject(error)
        })
    })
  }

  const handleSave = () => {
    const sessionId = sessionWS._id
    const url = '/api/rig/deleteRigsForSession'
    fetch(url, {
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: sessionId,
      }),
    })
      .then((response) => {
        response.json().then(() => {
          let promises = []
          listRigs.forEach((rig) => {
            delete rig._id

            promises.push(callAPICreateRig(rig))
          })
          Promise.all(promises)
            .then((rigs) => {
              //navigateTo('/oneSessionStats/')
              let maxSpeedRigId = '0'
              let maxSpeedBoardId = '0'
              let maxSpeedSailId = '0'
              rigs.forEach((rig) => {
                if (
                  sessionWS.maxSpeedTime >= rig.start &&
                  sessionWS.maxSpeedTime <= rig.end
                ) {
                  maxSpeedRigId = rig._id
                  maxSpeedBoardId = rig.boardId
                  maxSpeedSailId = rig.sailId
                }
              })

              const rigUrl = '/api/sessionWS/updateSessionMaxSpeedRig'
              fetch(rigUrl, {
                method: 'post',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  sessionId,
                  maxSpeedRigId,
                }),
              })
                .then((response) => {
                  response.json().then(() => {
                    console.log('session updated with max speed rig id')
                    changeSessionWS({
                      ...sessionWS,
                      maxSpeedRigId,
                      maxSpeedBoardId,
                      maxSpeedSailId,
                    })
                    const temp = Array.from(listSessionsWS.data)
                    const temp_index = temp.findIndex(
                      (session) => session._id === sessionWS._id,
                    )
                    temp[temp_index] = {
                      ...sessionWS,
                      maxSpeedRigId,
                      maxSpeedBoardId,
                      maxSpeedSailId,
                    }
                    setListSessionsWS({
                      type: 'CHANGED_SESSION_RESTR',
                      payload: temp,
                    })
                  })
                })
                .catch((error) => {
                  console.log(error)
                })

              handleUpdateStats(kRigsUsedView)
            })
            .catch((error) => {
              if (error.response) {
                console.log('Error creating rigs for session ' + sessionWS._id)
                console.log(error.response.data)
                console.log(error.response.status)
                console.log(error.response.headers)
              }
            })
        })
      })
      .catch((error) => {
        console.log('Error deleting rigs for session ' + sessionWS._id, error)
        setIsLoading(false)
      })
  }

  const handleHelp = (what) => {
    setShowHelp(what)
  }

  return isLoading ? (
    <LoaderWrapper>
      <Loader data-testid="loader" />
    </LoaderWrapper>
  ) : (
    <section className="section pt-0" ref={firstDiv}>
      {!showHelp && <Help handleHelp={handleHelp} />}
      {showHelp && (
        <HelpNotif handleHelp={handleHelp} helpText={choosenLang.help} />
      )}
      {chartOptions && sliderValues && rigsTimeDomain && (
        <>
          <RigsChart
            chartOptions={chartOptions}
            sliderValues={sliderValues}
            timeDomain={rigsTimeDomain.restricted}
            handleSliderChange={handleSliderChange}
          />
          <RigsOptions
            listRigs={listRigs}
            handleAddRig={handleAddRig}
            handleDelRig={handleDelRig}
            handleRigsUpdate={handleRigsUpdate}
            canBeSaved={canBeSaved}
            handleSave={handleSave}
            disabledDemo={user.userName === demoUserName}
          />
        </>
      )}
    </section>
  )
}

export default memo(Rigs)
